"use client";

import * as React from "react";
import { ContextMenu as CM } from "radix-ui";
import { ChevronDown, Copy, X } from "lucide-react";
import { HOME_ID, MAX_TABS, useTabs, type Tab } from "@/core/tabs";
import { SCREEN_META } from "@/core/screen-meta";
import { cn } from "@/lib/utils";
import { menuContentClass, menuItemClass, menuSeparatorClass, Dropdown, DropdownContent, DropdownItem, DropdownTrigger, Shortcut } from "@/components/ui/menu";
import { Tip } from "@/components/ui/overlay";

export function WorkspaceTabs() {
  const tabs = useTabs((s) => s.tabs);
  const activeId = useTabs((s) => s.activeId);
  const stripRef = React.useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = React.useState(false);

  React.useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    const check = () => setOverflow(el.scrollWidth > el.clientWidth + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [tabs.length]);

  React.useEffect(() => {
    stripRef.current?.querySelector<HTMLElement>(`[data-tab-id="${activeId}"]`)?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [activeId]);

  return (
    <div data-print-hide className="flex h-[34px] shrink-0 items-end border-b border-border bg-tabs pl-1">
      <div ref={stripRef} role="tablist" aria-label="Abas de trabalho" className="flex h-full min-w-0 flex-1 items-end overflow-x-hidden">
        {tabs.map((t) => (
          <TabItem key={t.id} tab={t} active={t.id === activeId} />
        ))}
      </div>
      <Dropdown>
        <Tip content={`Todas as abas (${tabs.length}/${MAX_TABS})`}>
          <DropdownTrigger asChild>
            <button
              type="button"
              aria-label="Todas as abas"
              className={cn("mx-1 mb-1 inline-flex size-6 items-center justify-center rounded-sm text-muted outline-none hover:bg-surface hover:text-fg focus-visible:ring-1 focus-visible:ring-focus", overflow && "text-primary")}
            >
              <ChevronDown className="size-4" />
            </button>
          </DropdownTrigger>
        </Tip>
        <DropdownContent align="end" className="max-h-[70vh] min-w-60 overflow-auto">
          {tabs.map((t) => {
            const Icon = SCREEN_META[t.screen].icon;
            return (
              <DropdownItem key={t.id} onSelect={() => useTabs.getState().activate(t.id)} className={cn(t.id === activeId && "font-medium text-primary")}>
                <Icon />
                <span className="truncate">{t.title}</span>
                {t.dirty ? <span className="ml-auto size-1.5 rounded-full bg-primary" /> : null}
              </DropdownItem>
            );
          })}
        </DropdownContent>
      </Dropdown>
    </div>
  );
}

function TabItem({ tab, active }: { tab: Tab; active: boolean }) {
  const s = useTabs.getState;
  const Icon = SCREEN_META[tab.screen].icon;
  const home = tab.id === HOME_ID;
  return (
    <CM.Root>
      <CM.Trigger asChild>
        <div
          role="tab"
          data-tab-id={tab.id}
          aria-selected={active}
          tabIndex={active ? 0 : -1}
          title={tab.title}
          onMouseDown={(e) => {
            if (e.button === 1) {
              e.preventDefault();
              if (!home) s().close(tab.id);
            } else if (e.button === 0) s().activate(tab.id);
          }}
          onKeyDown={(e) => {
            if (e.key === "ArrowRight") s().cycle(1);
            if (e.key === "ArrowLeft") s().cycle(-1);
          }}
          className={cn(
            "group relative flex h-[30px] shrink-0 cursor-default select-none items-center gap-1.5 rounded-t-md border border-b-0 pr-1 pl-2.5 text-sm outline-none",
            "focus-visible:ring-1 focus-visible:ring-inset focus-visible:ring-focus",
            home ? "pr-2.5" : "max-w-52 min-w-24",
            active
              ? "z-[1] -mb-px h-[31px] border-border bg-surface-2 font-medium text-fg shadow-[inset_0_2px_0_var(--primary)]"
              : "border-transparent text-muted hover:bg-surface/60 hover:text-fg",
          )}
        >
          <Icon className={cn("size-3.5 shrink-0", active ? "text-primary" : "text-subtle")} />
          {home ? null : <span className="min-w-0 flex-1 truncate">{tab.title}</span>}
          {home ? <span className="sr-only">{tab.title}</span> : null}
          {!home ? (
            <span className="relative inline-flex size-5 shrink-0 items-center justify-center">
              {tab.dirty ? <span className="absolute size-1.5 rounded-full bg-primary group-hover:hidden" aria-label="Não salvo" /> : null}
              <button
                type="button"
                tabIndex={-1}
                aria-label="Fechar aba"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => {
                  e.stopPropagation();
                  s().close(tab.id);
                }}
                className={cn(
                  "inline-flex size-5 items-center justify-center rounded-sm text-subtle hover:bg-row-hover hover:text-fg",
                  tab.dirty ? "invisible group-hover:visible" : active ? "" : "invisible group-hover:visible",
                )}
              >
                <X className="size-3.5" />
              </button>
            </span>
          ) : null}
        </div>
      </CM.Trigger>
      <CM.Portal>
        <CM.Content className={menuContentClass}>
          <CM.Item className={menuItemClass} disabled={home} onSelect={() => s().close(tab.id)}>
            <X />
            Fechar
            <Shortcut>Ctrl+W</Shortcut>
          </CM.Item>
          <CM.Item className={menuItemClass} onSelect={() => s().closeOthers(tab.id)}>
            <span className="w-4" />
            Fechar outras
          </CM.Item>
          <CM.Item className={menuItemClass} onSelect={() => s().closeRight(tab.id)}>
            <span className="w-4" />
            Fechar à direita
          </CM.Item>
          <CM.Separator className={menuSeparatorClass} />
          <CM.Item className={menuItemClass} disabled={home || s().tabs.length >= MAX_TABS} onSelect={() => s().duplicate(tab.id)}>
            <Copy />
            Duplicar
          </CM.Item>
        </CM.Content>
      </CM.Portal>
    </CM.Root>
  );
}
